import type { HeatMap } from '~/_shared/types/heatmap/heatMap.types';
import { convertColorToWebGLColor } from '~/_shared/utils/colors/colors.helpers';
import { type HeatMapItem } from '~/store/mapSettings/heatmaps/useHeatmapItems.selector';

type RenderedHeatmap = Readonly<{
  overlayHeatmap: WebglOverlayHeatmap;
}>;
type HeatMapLayer = Map<string, RenderedHeatmap>;

type HeatMapPoints = ReadonlyArray<HeatmapPoint>;

export class HeatMapsManager {
  private readonly heatmapsLayer: HeatmapLayer;
  private heatMapLayers: HeatMapLayer = new Map();

  constructor(heatmapsLayer: HeatmapLayer) {
    this.heatmapsLayer = heatmapsLayer;
  }

  destroy() {
    for (const layer of this.heatMapLayers.values()) {
      this.heatmapsLayer.remove(layer.overlayHeatmap);
    }
    this.heatMapLayers = new Map();
  }

  removeHeatmap(heatmapId: string) {
    const layer = this.heatMapLayers.get(heatmapId);
    if (!layer) {
      return;
    }
    this.heatmapsLayer.remove(layer.overlayHeatmap);
    this.heatMapLayers.delete(heatmapId);
  }

  createOrUpdateHeatmapItem(item: HeatMapItem, points: HeatMapPoints, zIndex: number) {
    let existing = this.heatMapLayers.get(item.heatmap.id);

    // workround for https://app.asana.com/0/1186320334640388/1200141506004053
    if (existing?.overlayHeatmap?.points !== points) {
      this.removeHeatmap(item.heatmap.id);
      existing = undefined;
    }

    if (existing) {
      this.updateHeatmapItem(item, points, zIndex);
    }
    else {
      this.createHeatmapItem(item, points, zIndex);
    }
  }

  private updateHeatmapItem(item: HeatMapItem, points: HeatMapPoints, zIndex: number) {
    const { heatmap } = item;
    const rendered = this.heatMapLayers.get(heatmap.id);
    if (!rendered) {
      return;
    }

    const overlayHeatmap = rendered.overlayHeatmap;

    overlayHeatmap.colors = getHeatmapWebGLColors(heatmap);
    overlayHeatmap.singleColor = !heatmap.isGradient;
    overlayHeatmap.opacity = heatmap.opacity / 100;
    overlayHeatmap.radius = getNormalizedRadius(heatmap.radius);
    overlayHeatmap.dissipate = heatmap.dissipate;
    overlayHeatmap.points = points;
    overlayHeatmap.maxIntensity = getMaxIntensityForHeatmapFromThreshold(heatmap.threshold, !heatmap.isGradient);
    overlayHeatmap.visible = item.otherOptions.visible;
    overlayHeatmap.zIndex = zIndex;
  }

  private createHeatmapItem(item: HeatMapItem, points: HeatMapPoints, zIndex: number) {
    if (this.heatMapLayers.has(item.heatmap.id)) {
      return;
    }

    const overlayHeatmap = createOverlayHeatmap(item, points, zIndex);

    this.heatmapsLayer.add(overlayHeatmap);
    this.heatMapLayers.set(item.heatmap.id, { overlayHeatmap });
  }
}

const createOverlayHeatmap = (item: HeatMapItem, points: HeatMapPoints, zIndex: number) => {
  const { heatmap } = item;

  const colors = getHeatmapWebGLColors(heatmap);

  return new WebGLOverlay.Heatmap({
    points,
    dissipate: heatmap.dissipate,
    radius: getNormalizedRadius(heatmap.radius),
    opacity: heatmap.opacity / 100,
    maxIntensity: getMaxIntensityForHeatmapFromThreshold(heatmap.threshold, !heatmap.isGradient),
    colors,
    visible: item.otherOptions.visible,
    zIndex,
  });
};

const getMaxIntensityForHeatmapFromThreshold = (threshold: number, isSingleColor: boolean): number => (
  threshold
  * (isSingleColor ? 0.003 : 0.01) // single color uses different rendering algo so we need to adjust
  + (isSingleColor ? 0 : 0.15)
  + 0.000001 // 0 threshold causes undefined behavior in WebGL
);
const getNormalizedRadius = (radius: number): number => Math.round((radius / 150) * 100);

const getHeatmapWebGLColors = (heatmap: HeatMap): WebglColor[] => {
  if (heatmap.isGradient) {
    const colors = heatmap.colors.map((color, index) => convertColorToWebGLColor(
      color, index === 0 ? 0.15 : undefined
    ));
    return colors;
  }
  return [convertColorToWebGLColor(heatmap.singleColor)];
};
